import { Button } from '@/components/ui/button';
import { DateTimePicker } from "@/components/ui/dateTimePicker";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { gql, useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

const CREATE_INSPECTION = gql`
   mutation CreateInspection($input: CreateInspectionInput!) {
      createInspection(input: $input) {
         id
         scheduledDate
         scheduledTime
         location
         inspectionStatus
      }
   }
`;

const GET_SCHEDULED_TIMES = gql`
   query GetScheduledTimes($date: String!) {
      getInspections {
         scheduledDate
         scheduledTime
         inspectionStatus
      }
   }
`;

const CHECK_OOP_STATUS = gql`
   query CheckOOPStatus($applicationId: ID!) {
      getOOPsByApplicationId(applicationId: $applicationId) {
         OOPstatus
      }
   }
`;

const TS_ScheduleInspectionModal = ({ isOpen, onClose, application, onScheduleComplete }) => {
   const [createInspection] = useMutation(CREATE_INSPECTION);
   const [isChrome, setIsChrome] = useState(false);
   const [scheduleData, setScheduleData] = useState({
      date: format(new Date(), "yyyy-MM-dd"),
      time: format(new Date(), "HH:mm"),
      location: "",
      additionalNotes: "",
   });
   const [selectedDate, setSelectedDate] = useState(null);
   const [selectedTime, setSelectedTime] = useState('');
   const [loading, setLoading] = useState(false);

   const { data: scheduledTimes, loading: loadingTimes } = useQuery(GET_SCHEDULED_TIMES, {
      variables: { date: selectedDate?.toISOString() || '' },
      skip: !selectedDate,
   });

   const { data: oopData } = useQuery(CHECK_OOP_STATUS, {
      variables: { applicationId: application?.id },
      skip: !application?.id || application?.applicationType !== 'Chainsaw Registration'
   });

   // Detect Chrome browser
   useEffect(() => {
      const isChromeBrowser = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      setIsChrome(isChromeBrowser);
   }, []);

   const handleDateChange = (e) => {
      setScheduleData(prev => ({
         ...prev,
         date: e.target.value
      }));
   };

   const handleTimeChange = (e) => {
      setScheduleData(prev => ({
         ...prev,
         time: e.target.value
      }));
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (!scheduleData.date || !scheduleData.time || !scheduleData.location) {
         toast.error("Please fill in all required fields");
         return;
      }

      // Add OOP validation for CSAW applications
      if (application?.applicationType === 'Chainsaw Registration') {
         const oopStatus = oopData?.getOOPsByApplicationId?.[0]?.OOPstatus;
         const isOOPComplete = ['Completed OOP', 'Issued OR'].includes(oopStatus);

         if (!isOOPComplete) {
            toast.error("Cannot schedule inspection for CSAW application without completed Order of Payment");
            return;
         }
      }

      try {
         // Combine date and time strings
         const dateTimeStr = `${scheduleData.date}T${scheduleData.time}`;
         const selectedDate = new Date(dateTimeStr);

         const formattedDate = format(selectedDate, "yyyy-MM-dd");
         const formattedTime = format(selectedDate, "HH:mm");

         console.log("Selected in modal:", {
            dateTimeStr,
            selectedDate,
            formattedDate,
            formattedTime
         });

         const input = {
            permitId: application.id,
            scheduledDate: formattedDate,
            scheduledTime: formattedTime,
            location: scheduleData.location.trim(),
         };

         const response = await createInspection({
            variables: { input },
            onError: (error) => {
               // Extract the specific error message from the GraphQL error
               const errorMessage = error.message.includes('Failed to create inspection:')
                  ? error.message.split('Failed to create inspection:')[1].trim()
                  : error.message;

               toast.error(errorMessage || "Failed to schedule inspection");
            }
         });

         if (response.data) {
            toast.success("Inspection scheduled successfully");
            onScheduleComplete?.();
            onClose();
         }
      } catch (error) {
         console.error("Error scheduling inspection:", error);
         // Extract error message from GraphQL error if available
         const errorMessage = error.message.includes('Failed to create inspection:')
            ? error.message.split('Failed to create inspection:')[1].trim()
            : "Failed to schedule inspection";

         toast.error(errorMessage);
      }
   };

   const handleSchedule = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
         // Check for conflicts
         const formattedDate = format(selectedDate, 'yyyy-MM-dd');
         const existingSchedule = scheduledTimes?.getInspections.find(
            insp =>
               insp.scheduledTime === selectedTime &&
               format(new Date(insp.scheduledDate), 'yyyy-MM-dd') === formattedDate &&
               ['Pending', 'Rescheduled'].includes(insp.inspectionStatus)
         );

         if (existingSchedule) {
            toast.error('This time slot is already booked. Please select a different time.');
            return;
         }

         await onScheduleComplete({
            variables: {
               input: {
                  permitId: application.id,
                  scheduledDate: formattedDate,
                  scheduledTime: selectedTime,
                  location: scheduleData.location.trim(),
               }
            }
         });

         toast.success('Inspection scheduled successfully');
         onClose();
      } catch (error) {
         toast.error(error.message);
      } finally {
         setLoading(false);
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-md">
            <DialogHeader>
               <DialogTitle>Schedule Inspection</DialogTitle>
               <DialogDescription>
                  Schedule inspection for application {application?.applicationNumber}
               </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
               <div className="space-y-4 py-4">
                  {isChrome ? (
                     // Native date and time inputs for Chrome
                     <div className="space-y-4">
                        <div className="space-y-2">
                           <Label htmlFor="date">Date</Label>
                           <Input
                              type="date"
                              id="date"
                              value={scheduleData.date}
                              onChange={handleDateChange}
                              min={format(new Date(), "yyyy-MM-dd")}
                              required
                           />
                        </div>
                        <div className="space-y-2">
                           <Label htmlFor="time">Time</Label>
                           <Input
                              type="time"
                              id="time"
                              value={scheduleData.time}
                              onChange={handleTimeChange}
                              required
                           />
                        </div>
                     </div>
                  ) : (
                     // ShadCN DateTimePicker for other browsers
                     <div className="space-y-2">
                        <Label>Date and Time</Label>
                        <DateTimePicker
                           date={new Date(`${scheduleData.date}T${scheduleData.time}`)}
                           setDate={(newDate) => {
                              setScheduleData(prev => ({
                                 ...prev,
                                 date: format(newDate, "yyyy-MM-dd"),
                                 time: format(newDate, "HH:mm")
                              }));
                           }}
                        />
                     </div>
                  )}

                  <div className="space-y-2">
                     <Label htmlFor="location">Location</Label>
                     <Input
                        id="location"
                        value={scheduleData.location}
                        onChange={(e) => setScheduleData(prev => ({
                           ...prev,
                           location: e.target.value
                        }))}
                        placeholder="Enter inspection location"
                        required
                     />
                  </div>

                  <div className="space-y-2">
                     <Label htmlFor="notes">Additional Notes</Label>
                     <Textarea
                        id="notes"
                        value={scheduleData.additionalNotes}
                        onChange={(e) => setScheduleData(prev => ({
                           ...prev,
                           additionalNotes: e.target.value
                        }))}
                        placeholder="Any additional notes or instructions..."
                     />
                  </div>
               </div>

               <DialogFooter>
                  <Button variant="outline" type="button" onClick={onClose}>
                     Cancel
                  </Button>
                  <Button type="submit">Schedule Inspection</Button>
               </DialogFooter>
            </form>
         </DialogContent>
      </Dialog>
   );
};

export default TS_ScheduleInspectionModal;
